import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import {
  archiveVodAsset,
  fetchVodAsset,
  fetchVodRenditionPreview,
  fetchVodTypes,
  publishVodAsset,
  resendVodToAmagi,
  retryVodPackaging,
  unarchiveVodAsset,
  unpublishVodAsset,
  updateVodAsset,
  linkVodAssetMatchByExtEventId,
  fetchSeries,
  updateVodAssetEpisode,
  updateVodAssetMatch,
  updateVodAssetPlayers,
  updateVodAssetSeasons,
} from '../services/vodApi'
import { api, ConflictError } from '../services/api'
import {
  Badge,
  Banner,
  Button,
  Panel,
  ImageManagement,
  PublishingAuditModal,
  SlugManager,
  StreamPlayer,
  ToggleGroup,
  VodPipelinePanel,
  VodUploadPanel,
} from '../components'
import { CompetitorSelect, SeasonSelect } from '../components'
import type { StreamPlayerError } from '../components'
import { Spinner } from '../components/Spinner'
import type { Competitor, Season } from '../types/forms'
import { useToast } from '../contexts/ToastContext'
import { useSpeedbump } from '../components/Speedbump'
import {
  convertPTInputToUTC,
  convertUTCToPTInput,
  formatToPacificTime,
} from '../utils/timezone'
import { UUID_PATTERN } from '../utils/uuid'
import {
  formatDuration,
  formatTimecode,
  normalizeTimecode,
  parseTimecode,
} from '../utils/duration'
import { versionFromKey, versionFromRendition } from '../utils/vodVersion'
import {
  VodAsset,
  VodPublishState,
  VodRendition,
  VodRenditionPreview,
} from '../types/vod'
import pageStyles from '../styles/page.module.scss'
import selectStyles from '../styles/react-select.module.scss'
import styles from './VodDetail.module.scss'
import {
  isUnrecognizedOrigin,
  originLabel,
  originVariant,
} from '../utils/vodOrigin'

type GatingChoice = 'inherit' | 'gated' | 'free'

const gatingOf = (requiresSubscription?: boolean | null): GatingChoice =>
  requiresSubscription == null ? 'inherit' : requiresSubscription ? 'gated' : 'free'

const GATING_OPTIONS = [
  { value: 'inherit' as const, label: 'Type default' },
  { value: 'gated' as const, label: 'Gated' },
  { value: 'free' as const, label: 'Free' },
]

// Postgres INTEGER, the type of both trim-point columns.
const MAX_TIMECODE_SEC = 2_147_483_647

const DEFAULT_EDITABLE = [
  'tc_title',
  'tc_description',
  'tc_synopsis',
  'display_date',
  'clip_start_sec',
  'clip_end_sec',
]

// Summary of the linked match, read straight off the VOD payload.
function matchLabel(m: {
  home_competitor_name?: string | null
  away_competitor_name?: string | null
  effective_round?: string | null
  effective_start_time?: string | null
  competition?: { name?: string | null } | null
}): string {
  const parts = [
    `${m.home_competitor_name || 'TBD'} vs ${m.away_competitor_name || 'TBD'}`,
  ]
  if (m.competition?.name) parts.push(m.competition.name)
  if (m.effective_round) parts.push(m.effective_round)
  if (m.effective_start_time) parts.push(m.effective_start_time.split('T')[0])
  return parts.join(' · ')
}

// Whether this browser plays HLS off a plain video element (Safari). Only a
// fallback for when Bitmovin can't start, e.g. an unlicensed domain.
const NATIVE_HLS =
  typeof document !== 'undefined' &&
  !!document.createElement('video').canPlayType('application/vnd.apple.mpegurl')

const PUBLISH_STATE_VARIANT: Record<
  VodPublishState,
  'success' | 'warning' | 'error' | 'neutral'
> = {
  ACTIVE: 'success',
  SCHEDULED: 'warning',
  AWAITING_PACKAGING: 'warning',
  VIRTUAL: 'neutral',
  HIDDEN: 'neutral',
  EXPIRED: 'error',
}

const PUBLISH_STATE_LABEL: Record<VodPublishState, string> = {
  ACTIVE: 'Published',
  SCHEDULED: 'Scheduled',
  AWAITING_PACKAGING: 'Awaiting packaging',
  VIRTUAL: 'Virtual placeholder',
  HIDDEN: 'Unpublished',
  EXPIRED: 'Expired',
}

const AVAILABILITY_VARIANT: Record<
  string,
  'success' | 'warning' | 'error' | 'neutral'
> = {
  AVAILABLE: 'success',
  UPCOMING: 'warning',
  EXPIRED: 'error',
  UNKNOWN: 'neutral',
}

type PreviewKind = 'MP4' | 'HLS' | 'DASH'

interface PreviewSource {
  id: string
  kind: PreviewKind
  url: string
}

// A rendition alongside the source to preview it from, when there is one.
interface RenditionTab {
  id: string
  label: string
  rendition: VodRendition | null
  preview: PreviewSource | null
}

const PREVIEW_ORDER: Record<PreviewKind, number> = { MP4: 0, HLS: 1, DASH: 2 }

const isPreviewKind = (kind: string): kind is PreviewKind =>
  kind === 'MP4' || kind === 'HLS' || kind === 'DASH'

// One tab per rendition, previewable kinds first so the tab that opens by
// default is one that plays. MP4 plays off the video element; the manifest
// kinds go through Bitmovin.
function renditionTabs(asset: VodAsset): RenditionTab[] {
  const tabs: RenditionTab[] = (asset.renditions || []).map((r) => {
    const kind = r.rendition_kind_code
    return {
      id: r.tc_vod_rendition_id,
      label: kind,
      rendition: r,
      preview:
        isPreviewKind(kind) && r.cdn_url
          ? { id: r.tc_vod_rendition_id, kind, url: r.cdn_url }
          : null,
    }
  })

  // Legacy MP4-shaped video field, when no MP4 rendition carries a URL. It
  // attaches to a URL-less MP4 rendition if one exists rather than opening a
  // second tab under the same label.
  const legacyUrl = asset.video?.cdn_url
  if (legacyUrl && !tabs.some((t) => t.preview?.kind === 'MP4')) {
    const urllessMp4 = tabs.find((t) => t.label === 'MP4')
    if (urllessMp4) {
      urllessMp4.preview = { id: urllessMp4.id, kind: 'MP4', url: legacyUrl }
    } else {
      tabs.push({
        id: 'legacy-mp4',
        label: 'MP4',
        rendition: null,
        preview: { id: 'legacy-mp4', kind: 'MP4', url: legacyUrl },
      })
    }
  }

  const rank = (t: RenditionTab) =>
    t.preview ? PREVIEW_ORDER[t.preview.kind] : PREVIEW_ORDER.DASH + 1
  return tabs.sort((a, b) => rank(a) - rank(b))
}

// Why a tab has no player, when the "Not playable" badge hasn't said it
// already. Null leaves the badge to speak for itself.
function noPreviewReason(tab: RenditionTab): string | null {
  if (tab.rendition && !tab.rendition.is_playable) return null
  if (tab.rendition && !isPreviewKind(tab.rendition.rendition_kind_code)) {
    return 'This rendition kind cannot be played in the browser.'
  }
  return 'This rendition has no CDN URL yet.'
}

// A 409 from the publish endpoints carries its user-facing prose at
// detail.message; ConflictError.message is the whole detail object flattened.
function conflictMessage(err: unknown, fallback: string): string {
  if (err instanceof ConflictError) {
    const message = err.data?.detail?.message
    if (typeof message === 'string' && message) return message
  }
  return (err as Error)?.message || fallback
}

type ManifestKind = Exclude<PreviewKind, 'MP4'>

const isManifestKind = (kind: string): kind is ManifestKind =>
  kind === 'HLS' || kind === 'DASH'

interface ManifestPreviewProps {
  assetId: string
  kind: ManifestKind
}

// Plays a packaged HLS or DASH rendition through an edge grant minted by the
// API. The catalog cdn_url alone stops playing once the VOD CDN enforces
// grants, so the URL comes from the API, which signs with the same key ACE
// does. A player failure re-mints once, covering a grant that expired while
// the page sat open; a second failure falls back to native HLS (Safari) or a
// link. Mount it keyed on the rendition AND its catalog URL: packaging repoints
// the same rendition row at a new version in place, and the new URL must start
// the preview over rather than keep playing the old version's grant.
const ManifestPreview: React.FC<ManifestPreviewProps> = ({ assetId, kind }) => {
  const [grant, setGrant] = useState<VodRenditionPreview | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [playerError, setPlayerError] = useState<StreamPlayerError | null>(null)
  const [nativeFailed, setNativeFailed] = useState(false)
  const retriedRef = useRef(false)
  // The URL whose player we are still listening to. A retry clears it so a late
  // duplicate report from the failed instance cannot cancel the fresh attempt.
  const listeningUrlRef = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchVodRenditionPreview(assetId, kind)
      .then((next) => {
        if (cancelled) return
        listeningUrlRef.current = next.url
        setGrant(next)
      })
      .catch((err) => {
        if (!cancelled) {
          setFetchError((err as Error).message || 'Failed to fetch the preview URL')
        }
      })
    return () => {
      cancelled = true
    }
  }, [assetId, kind])

  const handlePlayerError = (url: string, err: StreamPlayerError) => {
    if (url !== listeningUrlRef.current) return
    if (!retriedRef.current) {
      retriedRef.current = true
      listeningUrlRef.current = null
      fetchVodRenditionPreview(assetId, kind)
        .then((next) => {
          listeningUrlRef.current = next.url
          setGrant(next)
          setAttempt((n) => n + 1)
        })
        .catch(() => setPlayerError(err))
      return
    }
    // One failure can be reported twice — a typed player event and an untyped
    // load() rejection — in either order. Keep whichever carries a Bitmovin
    // code, since that is the report with a real diagnosis.
    setPlayerError((prev) => (prev?.code !== undefined ? prev : err))
  }

  if (fetchError) {
    return (
      <div className={styles.previewFallback}>
        <Banner variant="error" message={fetchError} />
      </div>
    )
  }
  if (!grant) return <Spinner size="small" message="Fetching preview…" />

  const meta = (
    <div className={styles.previewMeta}>
      <span>
        {grant.drm
          ? `DRM-protected — this preview's license token expires ${formatToPacificTime(grant.drm.expires_at)}.`
          : grant.minted && grant.expires_at
            ? `Preview link expires ${formatToPacificTime(grant.expires_at)}.`
            : 'Vendor-hosted rendition — the catalog URL plays as-is.'}
      </span>
    </div>
  )

  if (playerError === null) {
    const url = grant.url
    return (
      <>
        <StreamPlayer
          key={`${attempt}-${url}`}
          url={url}
          format={kind}
          drm={grant.drm}
          className={styles.previewStage}
          onError={(err) => handlePlayerError(url, err)}
        />
        {meta}
      </>
    )
  }

  // Safari plays HLS off the video element, so a Bitmovin failure (an
  // unlicensed domain, say) doesn't have to cost a preview that would work.
  if (kind === 'HLS' && NATIVE_HLS && !nativeFailed) {
    return (
      <>
        <video
          key={`${attempt}-${grant.url}`}
          className={styles.player}
          src={grant.url}
          controls
          preload="metadata"
          onError={() => setNativeFailed(true)}
        >
          Your browser does not support inline video.
        </video>
        <p className={styles.previewHint}>
          Bitmovin could not start ({playerError.message}) — playing through this
          browser&apos;s native HLS support instead.
        </p>
        {meta}
      </>
    )
  }

  return (
    <div className={styles.previewFallback}>
      <p>{playerError.message}</p>
      <a href={grant.url} target="_blank" rel="noopener noreferrer">
        Open {kind} manifest ↗
      </a>
      {meta}
    </div>
  )
}

const VodDetail: React.FC = () => {
  const { vodId } = useParams<{ vodId: string }>()
  const { toast } = useToast()
  const confirm = useSpeedbump()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [synopsis, setSynopsis] = useState('')
  // Held as a Pacific datetime-local value; converted at the API boundary.
  const [displayDate, setDisplayDate] = useState('')
  const [vodTypeCode, setVodTypeCode] = useState('')
  const [gating, setGating] = useState<GatingChoice>('inherit')
  // Trim points as typed timecodes, parsed at save. Blank means no trim on that side.
  const [clipStart, setClipStart] = useState('')
  const [clipEnd, setClipEnd] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  // MP4 previews play off the video element; a failed load swaps in a link.
  // Manifest previews keep their own state inside ManifestPreview.
  const [nativeFailed, setNativeFailed] = useState(false)
  // Which asset the editable fields were last seeded from, so a rebuilt payload
  // for the same asset cannot overwrite what the editor has typed.
  const seededAssetIdRef = useRef<string | null>(null)
  const [isRetryingPackaging, setIsRetryingPackaging] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [isTogglingPublish, setIsTogglingPublish] = useState(false)
  const [isTogglingArchive, setIsTogglingArchive] = useState(false)
  const navigate = useNavigate()
  const [selectedTabIdx, setSelectedTabIdx] = useState(0)
  const [selectedMerchTags, setSelectedMerchTags] = useState<string[]>([])
  const [merchTagOptions, setMerchTagOptions] = useState<
    Array<{ value: string; label: string }>
  >([])
  const [isUpdatingMerchTags, setIsUpdatingMerchTags] = useState(false)
  const [isUpdatingPlayers, setIsUpdatingPlayers] = useState(false)
  const [isUpdatingSeasons, setIsUpdatingSeasons] = useState(false)
  const [seasonError, setSeasonError] = useState<string | null>(null)
  // Edits are no longer serialised by a disabled input, so two saves can be in
  // flight. Only the newest may write the cache — an earlier response landing
  // late would otherwise reinstate the list the editor just moved past.
  const playerSaveSeq = useRef(0)
  const seasonSaveSeq = useRef(0)
  const merchTagSaveSeq = useRef(0)
  const [playerError, setPlayerError] = useState<string | null>(null)
  const [merchTagError, setMerchTagError] = useState<string | null>(null)
  const [isEditingMatch, setIsEditingMatch] = useState(false)
  const [isSavingMatch, setIsSavingMatch] = useState(false)
  const [matchIdInput, setMatchIdInput] = useState('')
  const [matchIdError, setMatchIdError] = useState<string | null>(null)
  const [seriesId, setSeriesId] = useState<string>('')
  const [seasonId, setSeasonId] = useState<string>('')
  const [episodeNumber, setEpisodeNumber] = useState<string>('')
  const [episodeError, setEpisodeError] = useState<string | null>(null)
  const [showAuditModal, setShowAuditModal] = useState(false)

  const { data: asset, isLoading, error, mutate } = useSWR(
    vodId ? ['vod-asset-detail', vodId] : null,
    () => fetchVodAsset(vodId!),
    { revalidateOnFocus: false }
  )

  // Same SWR key the list and create pages use, so it is usually cache-warm.
  const { data: typesData, error: typesError } = useSWR(
    'vod-types',
    fetchVodTypes,
    { revalidateOnFocus: false }
  )
  const isSeriesEpisode = vodTypeCode === 'SERIES_EPISODE'

  // Only fetched when the panel can appear, so a non-episodic asset pays
  // nothing for it.
  const { data: seriesData, error: seriesError } = useSWR(
    isSeriesEpisode ? 'vod-series' : null,
    fetchSeries,
    { revalidateOnFocus: false }
  )
  const seriesOptions = useMemo(
    () =>
      (seriesData?.series || []).map((s) => ({
        value: s.tc_series_id,
        label: s.seasons.length ? s.title : `${s.title} (no seasons yet)`,
        isDisabled: s.seasons.length === 0,
      })),
    [seriesData]
  )
  // Which series the chosen season belongs to. Derived rather than stored, so
  // the two pickers cannot disagree about the series.
  const selectedSeries = useMemo(
    () =>
      (seriesData?.series || []).find((s) =>
        s.seasons.some((x) => x.tc_series_season_id === seasonId)
      ) || null,
    [seriesData, seasonId]
  )
  // A series the editor picked outright wins over the one derived from the
  // saved season, so choosing a new series empties the season picker.
  const activeSeriesId = seriesId || selectedSeries?.tc_series_id || ''
  const seasonOptions = useMemo(() => {
    const chosen = (seriesData?.series || []).find(
      (s) => s.tc_series_id === activeSeriesId
    )
    return (chosen?.seasons || []).map((x) => ({
      value: x.tc_series_season_id,
      label: x.year && x.title !== String(x.year) ? `${x.title} (${x.year})` : x.title,
    }))
  }, [seriesData, activeSeriesId])

  // Follows the Type control rather than the saved asset, so the gating hint
  // tracks an unsaved type change.
  const assetType = useMemo(
    () => (typesData?.types || []).find((t) => t.vod_type_code === vodTypeCode),
    [typesData, vodTypeCode]
  )
  const typeOptions = useMemo(() => {
    const options = (typesData?.types || []).map((t) => ({
      value: t.vod_type_code,
      label: t.display_name,
    }))
    // A deactivated type is absent from the option set but still on the asset;
    // carry it so the control shows the truth rather than an empty box.
    const current = asset?.vod_type_code
    if (current && !options.some((opt) => opt.value === current)) {
      options.push({
        value: current,
        label: asset?.vod_type_display_name || current,
      })
    }
    return options
  }, [typesData, asset])

  // The asset payload already carries the match, resolved. It used to be
  // searched for in the matches list instead, which filters to licensed
  // seasons and to a date window — so a VOD linked to an unlicensed match
  // showed "Match not found" for a link that was perfectly good.
  const linkedMatch = asset?.match ?? null
  const matchTcId = linkedMatch?.tc_match_id

  // Seed the editable fields from the payload, but only when the form is showing
  // a DIFFERENT asset. Keying this on the asset object instead meant every
  // identity change reseeded it — and the tag and player handlers deliberately
  // hand SWR a rebuilt asset (`mutate({...asset, tags})`) so their chips paint
  // immediately, so adding a tag threw away an unsaved type or title. Which is
  // the ordinary review sequence: classify the delivery, then tag it.
  useEffect(() => {
    const assetId = asset?.tc_vod_asset_id
    if (!assetId || seededAssetIdRef.current === assetId) return
    seededAssetIdRef.current = assetId
    setTitle(asset.tc_title || '')
    setDescription(asset.tc_description || '')
    setSynopsis(asset.tc_synopsis || '')
    setDisplayDate(convertUTCToPTInput(asset.display_date))
    setVodTypeCode(asset.vod_type_code || '')
    setClipStart(formatTimecode(asset.clip_start_sec))
    setClipEnd(formatTimecode(asset.clip_end_sec))
    setSeriesId('')
    setSeasonId(asset.episode?.tc_series_season_id || '')
    setEpisodeNumber(
      asset.episode?.episode_number != null
        ? String(asset.episode.episode_number)
        : ''
    )
    setEpisodeError(null)
    setGating(gatingOf(asset.requires_subscription))
    setSelectedMerchTags(asset.tags || [])
  }, [asset])

  // Preview state stays keyed on the payload: it is not the editor's input, and
  // a re-upload arriving through mutate() should clear a stale player error.
  useEffect(() => {
    if (asset) {
      setNativeFailed(false)
    }
  }, [asset])

  useEffect(() => {
    setSelectedTabIdx(0)
  }, [vodId])

  // Only the vocabulary needs fetching — the asset's own tags ride along in the
  // detail payload, so the chips paint with the rest of the panel.
  useEffect(() => {
    const loadTagOptions = async () => {
      try {
        const merchOptions = await api.get('/publishing/tag-options')
        setMerchTagOptions(merchOptions.tags || [])
      } catch {
        setMerchTagError('Failed to load tags')
      }
    }
    loadTagOptions()
  }, [])

  // Every competitor is editable, the linked match's included. A doubles
  // pairing is marked, since "Nadal R / Ruud CA" beside its own two players
  // reads as a fourth person otherwise.
  const chosenPlayers = useMemo(
    () =>
      (asset?.players || []).map((p) => ({
        tc_id: p.tc_competitor_id,
        // Editorial teams carry no Sports Radar id.
        ext_id: p.ext_competitor_id ?? '',
        name: (p.name || 'Unknown') + (p.is_team ? ' · team' : ''),
        team_type: p.is_team ? 'doubles' : 'singles',
      })),
    [asset]
  )

  const chosenSeasons = useMemo(
    () =>
      (asset?.seasons || []).map((s) => ({
        tc_id: s.tc_season_id,
        // The picker only ever reads tc_id; a season's ext id is not in the
        // payload because nothing displays it.
        ext_id: '',
        name: s.name || 'Unknown season',
        year: s.year ?? 0,
      })),
    [asset]
  )

  const handleUpdateSeasons = useCallback(
    async (seasons: Season[]) => {
      if (!vodId || !asset) return
      const seq = ++seasonSaveSeq.current
      const previous = asset
      setIsUpdatingSeasons(true)
      setSeasonError(null)

      mutate(
        {
          ...asset,
          seasons: seasons.map((s) => ({
            tc_season_id: s.tc_id,
            name: s.name,
            year: s.year,
          })),
        },
        { revalidate: false }
      )

      try {
        const res = await updateVodAssetSeasons(
          vodId,
          seasons.map((s) => s.tc_id)
        )
        if (seq !== seasonSaveSeq.current) return
        mutate(
          (current) => ({ ...(current ?? previous), seasons: res.seasons }),
          { revalidate: false }
        )
      } catch (err) {
        if (seq !== seasonSaveSeq.current) return
        mutate(previous, { revalidate: false })
        setSeasonError(
          err instanceof Error ? err.message : 'Failed to update seasons'
        )
      } finally {
        if (seq === seasonSaveSeq.current) setIsUpdatingSeasons(false)
      }
    },
    [vodId, asset, mutate]
  )

  const handleUpdatePlayers = useCallback(
    async (competitors: Competitor[]) => {
      if (!vodId || !asset) return
      const seq = ++playerSaveSeq.current
      const previous = asset
      setIsUpdatingPlayers(true)
      setPlayerError(null)

      // Paint the change now and let the request confirm it. The picker holds
      // the whole list, so what it hands back is the whole list.
      mutate(
        {
          ...asset,
          players: competitors.map((c) => ({
            tc_competitor_id: c.tc_id,
            ext_competitor_id: c.ext_id || null,
            // Strip the "· team" marker the label carries.
            name: c.name.replace(/ · team$/, ''),
            is_team: c.team_type === 'doubles',
            is_match_derived: false,
          })),
        },
        { revalidate: false }
      )

      try {
        const res = await updateVodAssetPlayers(
          vodId,
          competitors.map((c) => c.tc_id)
        )
        // A later edit has already replaced this one; its response is the
        // truth, and writing this stale list would undo it.
        if (seq !== playerSaveSeq.current) return
        mutate(
          (current) => ({ ...(current ?? previous), players: res.players }),
          { revalidate: false }
        )
      } catch (err) {
        if (seq !== playerSaveSeq.current) return
        mutate(previous, { revalidate: false })
        setPlayerError(
          err instanceof Error ? err.message : 'Failed to update players'
        )
      } finally {
        if (seq === playerSaveSeq.current) setIsUpdatingPlayers(false)
      }
    },
    [vodId, asset, mutate]
  )

  const handleUpdateMerchTags = useCallback(
    async (tags: string[]) => {
      if (!vodId) return
      const seq = ++merchTagSaveSeq.current
      const previousTags = selectedMerchTags
      const previousAsset = asset
      setIsUpdatingMerchTags(true)
      setMerchTagError(null)

      // Same as the players field: show the chips immediately. The field is
      // seeded from the payload, so the cache has to move with it or a revisit
      // repaints the pre-edit chips from a stale asset.
      setSelectedMerchTags(tags)
      if (asset) mutate({ ...asset, tags }, { revalidate: false })

      try {
        await api.put(`/vod/assets/${vodId}/merch-tags`, { tags })
      } catch (err) {
        if (seq !== merchTagSaveSeq.current) return
        setSelectedMerchTags(previousTags)
        if (previousAsset) mutate(previousAsset, { revalidate: false })
        setMerchTagError(
          err instanceof Error ? err.message : 'Failed to update merchandising tags'
        )
      } finally {
        if (seq === merchTagSaveSeq.current) setIsUpdatingMerchTags(false)
      }
    },
    [vodId, asset, selectedMerchTags, mutate]
  )

  const editableFields = asset?.capabilities?.editable_fields ?? DEFAULT_EDITABLE
  const canEdit = (field: string) => editableFields.includes(field)

  const savedSeasonId = asset?.episode?.tc_series_season_id || ''
  const savedEpisodeNumber =
    asset?.episode?.episode_number != null ? String(asset.episode.episode_number) : ''
  // Retyping away from Series Episode counts as a change even though the panel
  // is gone: the link has to be cleared, or the unique index goes on reserving
  // that episode number in that season against an asset that is no longer an
  // episode, and a real one can never take it.
  const episodeDirty = isSeriesEpisode
    ? seasonId !== savedSeasonId || episodeNumber.trim() !== savedEpisodeNumber
    : Boolean(asset?.episode)

  const hasChanges = useMemo(() => {
    if (!asset) return false
    return (
      title !== (asset.tc_title || '') ||
      description !== (asset.tc_description || '') ||
      synopsis !== (asset.tc_synopsis || '') ||
      displayDate !== convertUTCToPTInput(asset.display_date) ||
      vodTypeCode !== (asset.vod_type_code || '') ||
      gating !== gatingOf(asset.requires_subscription) ||
      clipStart !== formatTimecode(asset.clip_start_sec) ||
      clipEnd !== formatTimecode(asset.clip_end_sec) ||
      episodeDirty
    )
  }, [
    title,
    description,
    synopsis,
    displayDate,
    vodTypeCode,
    gating,
    clipStart,
    clipEnd,
    episodeDirty,
    asset,
  ])

  const titleValid = !canEdit('tc_title') || title.trim().length > 0
  // display_date is NOT NULL and orders every rail: an empty box is a mistake,
  // not a request to clear it.
  const displayDateValid = !canEdit('display_date') || displayDate.length > 0

  // Mirrors the server's rules; the server remains the authority.
  const resolveClipWindow = ():
    | { ok: true; start: number | null; end: number | null }
    | { ok: false; problem: string } => {
    const start = parseTimecode(clipStart)
    const end = parseTimecode(clipEnd)
    if (start === undefined)
      return { ok: false, problem: 'Start time must look like 1:04 or 1:44:06.' }
    if (end === undefined)
      return { ok: false, problem: 'Stop time must look like 1:04 or 1:44:06.' }
    if ((start ?? 0) > MAX_TIMECODE_SEC || (end ?? 0) > MAX_TIMECODE_SEC)
      return { ok: false, problem: 'That time is too large.' }
    if (end !== null && end <= 0)
      return { ok: false, problem: 'The stop time must be after 0:00.' }
    if (start !== null && end !== null && end <= start)
      return { ok: false, problem: 'The stop time must come after the start time.' }
    const duration = asset?.duration_sec
    if (duration) {
      if (start !== null && start >= duration)
        return {
          ok: false,
          problem: `The start time must be before the end of the file (${formatDuration(duration)}).`,
        }
      if (end !== null && end > duration)
        return {
          ok: false,
          problem: `The stop time cannot be past the end of the file (${formatDuration(duration)}).`,
        }
    }
    return { ok: true, start, end }
  }

  const handleSave = async () => {
    if (!vodId || !asset) return
    if (!titleValid) {
      toast('Title is required', 'error')
      return
    }
    if (!displayDateValid) {
      toast('Display date is required', 'error')
      return
    }
    // Checked only when the window itself changed, so a stored window that a
    // shorter re-upload has since invalidated does not block unrelated edits.
    const clipDirty =
      clipStart !== formatTimecode(asset.clip_start_sec) ||
      clipEnd !== formatTimecode(asset.clip_end_sec)
    const clipWindow = clipDirty ? resolveClipWindow() : null
    if (clipWindow && !clipWindow.ok) {
      toast(clipWindow.problem, 'error')
      return
    }
    // Resolved before anything is written, so a bad episode number stops the
    // save rather than leaving the other fields written and this one not.
    setEpisodeError(null)
    let episodeChange: { seasonId: string | null; number: number | null } | null = null
    if (episodeDirty) {
      const wanted = episodeNumber.trim()
      const parsed = Number(wanted)
      let problem: string | null = null
      if (!isSeriesEpisode || (!seasonId && !wanted)) {
        episodeChange = { seasonId: null, number: null }
      } else if (!seasonId) {
        problem = 'Pick a season, or clear the episode number to detach.'
      } else if (!wanted) {
        problem = 'Enter an episode number, or clear the season to detach.'
      } else if (!Number.isInteger(parsed) || parsed < 1) {
        problem = 'Episode number must be a whole number, 1 or greater.'
      } else {
        episodeChange = { seasonId, number: parsed }
      }
      if (problem) {
        // Both, like the title and display-date checks above: the field shows
        // it, and the toast carries it in case the panel is scrolled away.
        setEpisodeError(problem)
        toast(problem, 'error')
        return
      }
    }

    setIsSaving(true)
    try {
      const payload: {
        tc_title?: string
        tc_description?: string
        tc_synopsis?: string
        display_date?: string
        vod_type_code?: string
        requires_subscription?: boolean | null
        clip_start_sec?: number | null
        clip_end_sec?: number | null
      } = {}
      if (canEdit('tc_title')) payload.tc_title = title.trim()
      if (canEdit('tc_description')) payload.tc_description = description.trim()
      if (canEdit('tc_synopsis')) payload.tc_synopsis = synopsis.trim()
      if (canEdit('display_date') && displayDate)
        payload.display_date = convertPTInputToUTC(displayDate)
      // Only when actually changed: the server re-validates against active
      // types, so re-sending an untouched (since deactivated) type would 400
      // the whole save and lose the edits the user did make.
      if (canEdit('vod_type_code') && vodTypeCode !== asset.vod_type_code)
        payload.vod_type_code = vodTypeCode
      if (
        canEdit('requires_subscription') &&
        gating !== gatingOf(asset.requires_subscription)
      )
        payload.requires_subscription = gating === 'inherit' ? null : gating === 'gated'
      // Only the sides that changed: null clears a side, an omitted side stays.
      if (clipWindow) {
        if (canEdit('clip_start_sec') && clipWindow.start !== (asset.clip_start_sec ?? null))
          payload.clip_start_sec = clipWindow.start
        if (canEdit('clip_end_sec') && clipWindow.end !== (asset.clip_end_sec ?? null))
          payload.clip_end_sec = clipWindow.end
      }
      const res = await updateVodAsset(vodId, payload)
      // Cache the authoritative asset from the response (not the wrapper)
      let saved = res.asset
      if (episodeChange) {
        try {
          const result = await updateVodAssetEpisode(
            vodId,
            episodeChange.seasonId,
            episodeChange.number
          )
          saved = { ...saved, episode: result.episode ?? null }
          // Write the server's canonical values back into the inputs. The
          // seeding effect only ever fires once per asset, so "007" would
          // otherwise sit in the box against a saved 7 and leave the form
          // permanently dirty.
          setSeasonId(result.episode?.tc_series_season_id || '')
          setEpisodeNumber(
            result.episode ? String(result.episode.episode_number) : ''
          )
          setSeriesId('')
        } catch (episodeErr) {
          // Two endpoints, no transaction: the fields above really did save, so
          // say that rather than implying the whole save failed. The server owns
          // the duplicate message — it names the series and season.
          await mutate(saved, { revalidate: false })
          const message =
            (episodeErr as Error).message || 'Failed to save the episode link'
          setEpisodeError(message)
          toast(`Other changes saved, but the episode link failed: ${message}`, 'error')
          return
        }
      }
      await mutate(saved, { revalidate: false })
      // Canonical spelling back into the boxes: the seeding effect fires once
      // per asset, so a typed "64" would otherwise sit against a saved 1:04.
      setClipStart(formatTimecode(saved.clip_start_sec))
      setClipEnd(formatTimecode(saved.clip_end_sec))
      toast('VOD updated', 'success')
    } catch (err) {
      toast((err as Error).message || 'Failed to save changes', 'error')
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    if (!asset) return
    setTitle(asset.tc_title || '')
    setDescription(asset.tc_description || '')
    setSynopsis(asset.tc_synopsis || '')
    setDisplayDate(convertUTCToPTInput(asset.display_date))
    setVodTypeCode(asset.vod_type_code || '')
    setGating(gatingOf(asset.requires_subscription))
    setSeriesId('')
    setSeasonId(savedSeasonId)
    setEpisodeNumber(savedEpisodeNumber)
    setEpisodeError(null)
    setClipStart(formatTimecode(asset.clip_start_sec))
    setClipEnd(formatTimecode(asset.clip_end_sec))
  }

  // Either identifier: our UUID, or the Sportradar event id an editor is
  // more likely to have to hand. Which one it is decides the key the server
  // is asked by; it does not guess.
  const handleMatchChange = async (
    tcMatchId: string | null,
    extEventId?: string
  ) => {
    if (!vodId) return
    setIsSavingMatch(true)
    setMatchIdError(null)
    try {
      if (extEventId) {
        await linkVodAssetMatchByExtEventId(vodId, extEventId)
      } else {
        await updateVodAssetMatch(vodId, tcMatchId)
      }
      toast(tcMatchId || extEventId ? 'Match linked' : 'Match link removed', 'success')
      setIsEditingMatch(false)
      setMatchIdInput('')
      await mutate()
    } catch (err) {
      // Surfaced in the field rather than only as a toast: the id is still on
      // screen to correct, and the server's 404 is the real validation.
      const message = (err as Error).message || 'Failed to update match link'
      if (tcMatchId || extEventId) setMatchIdError(message)
      else toast(message, 'error')
    } finally {
      setIsSavingMatch(false)
    }
  }

  const handleLinkMatch = () => {
    const candidate = matchIdInput.trim()
    if (UUID_PATTERN.test(candidate)) {
      handleMatchChange(candidate)
      return
    }
    // "sr:sport_event:47608611", or the digits on their own.
    if (/^(sr:sport_event:)?\d+$/.test(candidate)) {
      handleMatchChange(null, candidate)
      return
    }
    setMatchIdError('Enter a match UUID or a Sportradar id.')
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading VOD</h2>
          <p>Failed to fetch VOD details: {(error as Error).message}</p>
          <Button variant="secondary" onClick={() => mutate()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  if (isLoading || !asset) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.loading}>
          <h3>Loading VOD...</h3>
          <p>Please wait while we fetch the VOD data.</p>
        </div>
      </div>
    )
  }

  const showSourceTab = !asset.is_virtual
  const masterRendition =
    asset.renditions?.find((r) => r.rendition_kind_code === 'SOURCE') ?? null
  const masterVersion = versionFromKey(masterRendition?.s3_key)
  const masterIsPackaged =
    asset.renditions?.some(
      (r) =>
        r.is_playable &&
        (masterVersion === null || versionFromRendition(r) === masterVersion)
    ) ?? false

  const tabs = renditionTabs(asset).filter(
    (t) => !(showSourceTab && t.rendition?.rendition_kind_code === 'SOURCE')
  )
  const sourceOffset = showSourceTab ? 1 : 0
  const totalTabs = tabs.length + sourceOffset
  const activeIdx = selectedTabIdx < totalTabs ? selectedTabIdx : 0
  const isSourceActive = showSourceTab && activeIdx === 0
  const activeTab = isSourceActive ? null : tabs[activeIdx - sourceOffset] || null
  const activeUrl = activeTab?.rendition?.cdn_url || activeTab?.preview?.url
  const activeNoPreviewReason = activeTab ? noPreviewReason(activeTab) : null
  const activeVersion = versionFromRendition(activeTab?.rendition)
  const activeVersionLags =
    activeVersion !== null && masterVersion !== null && activeVersion < masterVersion

  // The typed window, validated live and described in sentence form, so the
  // effect of an edit and any problem with it are visible before saving.
  const mediaDuration = asset.duration_sec || null
  const liveWindow = resolveClipWindow()
  const windowSummary = (() => {
    if (!liveWindow.ok) return null
    const { start, end } = liveWindow
    const from = start ?? 0
    if (mediaDuration === null) {
      if (from <= 0 && end === null) return 'Viewers see the whole file.'
      if (end === null) return `Viewers see from ${formatTimecode(from)} to the end of the file.`
      const head = from > 0 ? `from ${formatTimecode(from)} ` : ''
      return `Viewers see ${head}up to ${formatTimecode(end)} (${formatDuration(end - from)}).`
    }
    const to = end ?? mediaDuration
    const tail = mediaDuration - to
    if (from <= 0 && tail <= 0)
      return `Viewers see the whole file (${formatDuration(mediaDuration)}).`
    const head = `Viewers see ${formatDuration(to - from)}`
    if (from > 0 && tail > 0)
      return `${head}, skipping the first ${formatDuration(from)} and the last ${formatDuration(tail)}.`
    if (from > 0) return `${head}, skipping the first ${formatDuration(from)}.`
    return `${head}, skipping the last ${formatDuration(tail)}.`
  })()

  const nativeVideo = (url: string) => (
    <video
      key={url}
      className={styles.player}
      src={url}
      controls
      preload="metadata"
      onError={() => setNativeFailed(true)}
    >
      Your browser does not support inline video.
    </video>
  )

  const openLink = (url: string, label: string) => (
    <a href={url} target="_blank" rel="noopener noreferrer">
      Open {label} ↗
    </a>
  )

  const renderPreview = (preview: PreviewSource) => {
    if (preview.kind === 'MP4') {
      return nativeFailed ? (
        <div className={styles.previewFallback}>
          <p>This file could not be played inline.</p>
          {openLink(preview.url, 'MP4')}
        </div>
      ) : (
        nativeVideo(preview.url)
      )
    }

    if (!vodId) return null
    return (
      <ManifestPreview
        key={`${preview.id}-${preview.kind}-${preview.url}`}
        assetId={vodId}
        kind={preview.kind}
      />
    )
  }


  const handlePublish = async () => {
    if (!vodId) return
    setIsTogglingPublish(true)
    try {
      const res = await publishVodAsset(vodId)
      await mutate(res.asset, { revalidate: false })
      toast('VOD published', 'success')
    } catch (err) {
      toast(conflictMessage(err, 'Failed to publish this VOD'), 'error')
      await mutate()
    } finally {
      setIsTogglingPublish(false)
    }
  }

  const handleUnpublish = async () => {
    if (!vodId) return
    const ok = await confirm({
      title: 'Unpublish this VOD?',
      message:
        'It stops appearing in rails and playback until it is published again.',
      confirmLabel: 'Unpublish',
      cancelLabel: 'Keep published',
      confirmVariant: 'danger',
    })
    if (!ok) return
    setIsTogglingPublish(true)
    try {
      const res = await unpublishVodAsset(vodId)
      await mutate(res.asset, { revalidate: false })
      toast('VOD unpublished', 'success')
    } catch (err) {
      toast(conflictMessage(err, 'Failed to unpublish this VOD'), 'error')
      await mutate()
    } finally {
      setIsTogglingPublish(false)
    }
  }

  const handleArchive = async () => {
    if (!vodId) return
    const ok = await confirm({
      title: 'Archive this VOD?',
      message:
        'It moves from the VODs list to Archived VODs and cannot be published until it is unarchived.',
      confirmLabel: 'Archive',
      cancelLabel: 'Keep',
      confirmVariant: 'danger',
    })
    if (!ok) return
    setIsTogglingArchive(true)
    try {
      await archiveVodAsset(vodId)
      toast('VOD archived', 'success')
      navigate('/vods')
    } catch (err) {
      toast(conflictMessage(err, 'Failed to archive this VOD'), 'error')
      await mutate()
      setIsTogglingArchive(false)
    }
  }

  const handleUnarchive = async () => {
    if (!vodId) return
    setIsTogglingArchive(true)
    try {
      const res = await unarchiveVodAsset(vodId)
      await mutate(res.asset, { revalidate: false })
      toast('VOD unarchived', 'success')
    } catch (err) {
      toast(conflictMessage(err, 'Failed to unarchive this VOD'), 'error')
      await mutate()
    } finally {
      setIsTogglingArchive(false)
    }
  }

  const handleResendToAmagi = async () => {
    if (!vodId) return
    setIsResending(true)
    try {
      await resendVodToAmagi(vodId)
      toast('Republished to Amagi', 'success')
      await mutate()
    } catch (err) {
      toast((err as Error).message || 'Republish failed', 'error')
    } finally {
      setIsResending(false)
    }
  }

  const handleRetryPackaging = async () => {
    if (!vodId) return
    setIsRetryingPackaging(true)
    try {
      await retryVodPackaging(vodId)
      toast('Packaging queued for resubmission', 'success')
      await mutate()
    } catch (err) {
      toast((err as Error).message || 'Failed to retry packaging', 'error')
    } finally {
      setIsRetryingPackaging(false)
    }
  }

  const canPublish = !!asset.capabilities?.can_publish
  const canUnpublish = !!asset.capabilities?.can_unpublish
  const canArchive = !!asset.capabilities?.can_archive
  const canUnarchive = !!asset.capabilities?.can_unarchive
  const archiveBlockedReason = asset.capabilities?.archive_blocked_reason ?? null
  const publishBlockedReason = asset.capabilities?.publish_blocked_reason ?? null
  const publishState = asset.capabilities?.publish_state ?? null
  const supportsAmagiDelivery = !!asset.capabilities?.supports_amagi_delivery
  const visibilityBlockedReason =
    asset.capabilities?.visibility_blocked_reason ?? null

  // WSC sets avail_start to a delivery timestamp with no avail_end, so it
  // carries no window; only Amagi delivers a real start/end pair.
  const availabilityState = asset.availability_state
  const hasAvailWindow = !!availabilityState && availabilityState !== 'UNKNOWN'
  const availWindow = `${
    asset.avail_start ? formatToPacificTime(asset.avail_start) : '—'
  } → ${asset.avail_end ? formatToPacificTime(asset.avail_end) : '—'}`

  return (
    <div>
      <div className={pageStyles.pageHeader}>
        <h1>VOD Details</h1>
        <p>
          <code className={styles.idCode}>{asset.tc_vod_asset_id}</code>
        </p>
        <div className={pageStyles.headerButtons}>
          {canUnarchive ? (
            <Button
              variant="secondary"
              onClick={handleUnarchive}
              disabled={isTogglingArchive}
            >
              {isTogglingArchive ? 'Unarchiving…' : 'Unarchive'}
            </Button>
          ) : (
            <span title={archiveBlockedReason ?? undefined}>
              <Button
                variant="secondary"
                onClick={handleArchive}
                disabled={!canArchive || isTogglingArchive}
              >
                {isTogglingArchive ? 'Archiving…' : 'Archive'}
              </Button>
            </span>
          )}
        </div>
      </div>

      <div className={styles.detailContent}>
        {/* Left: VOD Metadata (editable + read-only, mirrors highlights),
            then Related Match */}
        <div className={styles.leftColumn}>
          <Panel margin="none">
            <Panel.Header>
              <h2>VOD Metadata</h2>
            </Panel.Header>
            <div className={styles.fieldGrid}>
              <div className={styles.fieldGroup}>
                <label>
                  Title{' '}
                  {canEdit('tc_title') && (
                    <span className={styles.required}>*</span>
                  )}
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={!canEdit('tc_title')}
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={!canEdit('tc_description')}
                  className={styles.textarea}
                  rows={3}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Synopsis</label>
                <textarea
                  value={synopsis}
                  onChange={(e) => setSynopsis(e.target.value)}
                  disabled={!canEdit('tc_synopsis')}
                  className={styles.textarea}
                  rows={3}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>
                  Display Date{' '}
                  {canEdit('display_date') && (
                    <span className={styles.required}>*</span>
                  )}
                </label>
                <input
                  type="datetime-local"
                  value={displayDate}
                  onChange={(e) => setDisplayDate(e.target.value)}
                  disabled={!canEdit('display_date')}
                  className={styles.input}
                />
                <p className={styles.fieldHint}>Pacific time</p>
              </div>

              <div className={styles.fieldGroup}>
                <label>Merchandising Tags</label>
                {merchTagError && (
                  <div className={styles.errorText}>{merchTagError}</div>
                )}
                <CreatableSelect
                  isMulti
                  options={merchTagOptions}
                  value={selectedMerchTags.map((tag) => {
                    const option = merchTagOptions.find((opt) => opt.value === tag)
                    return { value: tag, label: option?.label || tag }
                  })}
                  onChange={(selectedOptions) =>
                    handleUpdateMerchTags(
                      selectedOptions ? selectedOptions.map((opt) => opt.value) : []
                    )
                  }
                  isDisabled={isLoading}
                  className={selectStyles.reactSelect}
                  classNamePrefix="react-select"
                  placeholder="Type to create or select tags..."
                  formatCreateLabel={(inputValue) => `Create tag "${inputValue}"`}
                />
                {isUpdatingMerchTags && (
                  <p className={styles.fieldHint}>Saving…</p>
                )}
              </div>

              <div className={styles.fieldGroup}>
                <label>Asset ID</label>
                <input
                  type="text"
                  value={asset.tc_vod_asset_id}
                  disabled
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="vodTypeCode">Type</label>
                {canEdit('vod_type_code') && !typesError ? (
                  <Select
                    inputId="vodTypeCode"
                    value={
                      typeOptions.find((opt) => opt.value === vodTypeCode) || null
                    }
                    onChange={(option) => setVodTypeCode(option?.value || '')}
                    options={typeOptions}
                    className={selectStyles.reactSelect}
                    classNamePrefix="react-select"
                    isClearable={false}
                    isLoading={!typesData}
                  />
                ) : (
                  <>
                    <input
                      type="text"
                      value={asset.vod_type_display_name || asset.vod_type_code}
                      disabled
                      className={styles.input}
                    />
                    {canEdit('vod_type_code') && typesError && (
                      <p className={styles.fieldHint}>
                        Types failed to load — reload the page to edit.
                      </p>
                    )}
                  </>
                )}
              </div>

              {isSeriesEpisode && (
                <div className={styles.fieldGroup}>
                  <label htmlFor="seriesSeason">Series Season</label>
                  {seriesError ? (
                    <p className={styles.fieldHint}>
                      Series failed to load — reload the page to edit.
                    </p>
                  ) : seriesData && seriesOptions.length === 0 ? (
                    <p className={styles.fieldHint}>No series exist yet.</p>
                  ) : (
                    <>
                      <Select
                        inputId="series"
                        value={
                          seriesOptions.find((o) => o.value === activeSeriesId) || null
                        }
                        onChange={(option) => {
                          setSeriesId(option?.value || '')
                          setSeasonId('')
                        }}
                        options={seriesOptions}
                        placeholder="Series…"
                        className={selectStyles.reactSelect}
                        classNamePrefix="react-select"
                        isClearable
                        isLoading={!seriesData}
                        noOptionsMessage={() => 'No series found'}
                      />
                      <Select
                        inputId="seriesSeason"
                        value={seasonOptions.find((o) => o.value === seasonId) || null}
                        onChange={(option) => setSeasonId(option?.value || '')}
                        options={seasonOptions}
                        placeholder={
                          activeSeriesId ? 'Season…' : 'Pick a series first'
                        }
                        className={selectStyles.reactSelect}
                        classNamePrefix="react-select"
                        isClearable
                        isDisabled={!activeSeriesId}
                        noOptionsMessage={() => 'This series has no seasons yet'}
                      />
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={episodeNumber}
                        onChange={(e) => setEpisodeNumber(e.target.value)}
                        placeholder="Episode number"
                        className={styles.input}
                      />
                      {episodeError && (
                        <p className={styles.fieldHint}>{episodeError}</p>
                      )}
                      {!episodeError && asset.episode && (
                        <p className={styles.fieldHint}>
                          {asset.episode.series_title} · {asset.episode.season_title} ·
                          episode {asset.episode.episode_number}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className={styles.fieldGroup}>
                <label>Source</label>
                <div>
                  <Badge variant={originVariant(asset.origin)}>
                    {originLabel(asset.origin)}
                  </Badge>
                  {isUnrecognizedOrigin(asset.origin) && asset.source_vendor
                    ? ` (${asset.source_vendor})`
                    : ''}
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label>Match</label>
                {matchTcId ? (
                  <div className={styles.linkedMatch}>
                    <div className={styles.linkedMatchLabel}>
                      {linkedMatch ? (
                        <Link to={`/matches/${matchTcId}`}>
                          {matchLabel(linkedMatch)}
                        </Link>
                      ) : (
                        <Link to={`/matches/${matchTcId}`}>Linked match</Link>
                      )}
                    </div>
                    <div className={styles.linkedMatchIds}>
                      {asset.match?.ext_event_id && (
                        <span>
                          SR <code>{asset.match.ext_event_id}</code>
                        </span>
                      )}
                      <span>
                        TC <code>{matchTcId}</code>
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className={styles.emptyText}>No match linked.</div>
                )}

                {isEditingMatch ? (
                  <>
                    <div className={styles.linkedMatchEditor}>
                      <input
                        type="text"
                        value={matchIdInput}
                        onChange={(e) => {
                          setMatchIdInput(e.target.value)
                          setMatchIdError(null)
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && handleLinkMatch()}
                        placeholder="Match UUID or sr:sport_event:…"
                        disabled={isSavingMatch}
                        className={styles.input}
                        autoFocus
                      />
                      <Button size="small" onClick={handleLinkMatch} disabled={isSavingMatch}>
                        {isSavingMatch ? 'Linking…' : 'Link'}
                      </Button>
                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => {
                          setIsEditingMatch(false)
                          setMatchIdInput('')
                          setMatchIdError(null)
                        }}
                        disabled={isSavingMatch}
                      >
                        Cancel
                      </Button>
                    </div>
                    {matchIdError && (
                      <div className={styles.errorText}>{matchIdError}</div>
                    )}
                  </>
                ) : (
                  <div className={styles.linkedMatchActions}>
                    <Button
                      size="small"
                      variant="secondary"
                      onClick={() => setIsEditingMatch(true)}
                      disabled={isSavingMatch}
                    >
                      {matchTcId ? 'Change match' : 'Link a match'}
                    </Button>
                    {matchTcId && (
                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => handleMatchChange(null)}
                        disabled={isSavingMatch}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <div className={styles.fieldGroup}>
                <label>Players</label>
                {playerError && (
                  <div className={styles.errorText}>{playerError}</div>
                )}
                <CompetitorSelect
                  isMulti
                  teamType="mixed"
                  value={chosenPlayers.length > 0 ? chosenPlayers : null}
                  onChange={(value) =>
                    handleUpdatePlayers(
                      Array.isArray(value) ? value : value ? [value] : []
                    )
                  }
                  isDisabled={isLoading}
                  placeholder="Add a player or doubles team…"
                />
                <p className={styles.fieldHint}>
                  {isUpdatingPlayers
                    ? 'Saving…'
                    : matchTcId
                      ? 'Linking a match adds its competitors — a doubles match adds both teams and all four players. Remove any of them, though a Sportradar correction to this match can bring one back.'
                      : 'No match linked. Every competitor here is yours to manage.'}
                </p>
              </div>

              <div className={styles.fieldGroup}>
                <label>Seasons</label>
                {seasonError && (
                  <div className={styles.errorText}>{seasonError}</div>
                )}
                <SeasonSelect
                  isMulti
                  value={chosenSeasons.length > 0 ? chosenSeasons : null}
                  onChange={(value) =>
                    handleUpdateSeasons(
                      Array.isArray(value) ? value : value ? [value] : []
                    )
                  }
                  isDisabled={isLoading}
                  placeholder="Add a tournament season…"
                />
                <p className={styles.fieldHint}>
                  {isUpdatingSeasons
                    ? 'Saving…'
                    : matchTcId
                      ? "Linking a match adds its season. Remove it if it's wrong, though a Sportradar correction to this match can bring it back."
                      : 'No match linked. Every season here is yours to manage.'}
                </p>
              </div>

              {hasAvailWindow && (
                <div className={styles.fieldGroup}>
                  <label>
                    Window{' '}
                    <Badge variant={AVAILABILITY_VARIANT[availabilityState]}>
                      {availabilityState}
                    </Badge>
                  </label>
                  <input
                    type="text"
                    value={availWindow}
                    disabled
                    className={styles.input}
                  />
                </div>
              )}

              <div className={styles.fieldGroup}>
                <label>Duration</label>
                <input
                  type="text"
                  value={formatDuration(asset.duration_sec)}
                  disabled
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Playback window</label>
                <div className={styles.clipRow}>
                  <span className={styles.clipRowLabel}>Start at</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={clipStart}
                    onChange={(e) => setClipStart(e.target.value)}
                    onBlur={() => setClipStart(normalizeTimecode(clipStart))}
                    placeholder="0:00"
                    aria-label="Start playback at"
                    disabled={!canEdit('clip_start_sec')}
                    className={styles.input}
                  />
                </div>
                <div className={styles.clipRow}>
                  <span className={styles.clipRowLabel}>Stop at</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={clipEnd}
                    onChange={(e) => setClipEnd(e.target.value)}
                    onBlur={() => setClipEnd(normalizeTimecode(clipEnd))}
                    placeholder={mediaDuration ? formatTimecode(mediaDuration) : 'end of file'}
                    aria-label="Stop playback at"
                    disabled={!canEdit('clip_end_sec')}
                    className={styles.input}
                  />
                </div>
                {liveWindow.ok ? (
                  <p className={styles.fieldHint}>{windowSummary}</p>
                ) : (
                  <p className={styles.errorText}>{liveWindow.problem}</p>
                )}
              </div>

              <div className={styles.fieldGroup}>
                <label>Requires subscription</label>
                {canEdit('requires_subscription') ? (
                  <>
                    <ToggleGroup
                      options={GATING_OPTIONS}
                      value={gating}
                      onChange={setGating}
                      size="small"
                    />
                    {gating === 'inherit' && (
                      <p className={styles.fieldHint}>
                        {assetType
                          ? `Currently ${assetType.default_requires_subscription ? 'gated' : 'free'} for ${assetType.display_name}.`
                          : 'Inherits the type default.'}
                      </p>
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    value={
                      asset.requires_subscription == null
                        ? '—'
                        : asset.requires_subscription
                          ? 'Yes'
                          : 'No'
                    }
                    disabled
                    className={styles.input}
                  />
                )}
              </div>

              <div className={styles.fieldGroup}>
                <label>Created At</label>
                <input
                  type="text"
                  value={formatToPacificTime(asset.created_at)}
                  disabled
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Updated At</label>
                <input
                  type="text"
                  value={formatToPacificTime(asset.updated_at)}
                  disabled
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.actionButtons}>
              <Button
                onClick={handleSave}
                size="large"
                disabled={isSaving || !hasChanges || !titleValid}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              {hasChanges && (
                <Button
                  variant="secondary"
                  size="large"
                  onClick={handleReset}
                  disabled={isSaving}
                >
                  Reset Changes
                </Button>
              )}
            </div>
          </Panel>

          {/* Related Match — identical to Highlight & Condensed Match Details */}
          {matchTcId && (
            <Panel margin="none">
              <Panel.Header>
                <h2>Related Match</h2>
              </Panel.Header>
              {linkedMatch ? (
                <div className={styles.matchInfo}>
                  <div className={styles.matchRow}>
                    <strong>Players:</strong>
                    <span>
                      {linkedMatch.home_competitor_name || 'TBD'} vs{' '}
                      {linkedMatch.away_competitor_name || 'TBD'}
                    </span>
                  </div>
                  {linkedMatch.competition && (
                    <div className={styles.matchRow}>
                      <strong>Tournament:</strong>
                      <span>{linkedMatch.competition.name}</span>
                    </div>
                  )}
                  {linkedMatch.effective_round ? (
                    <div className={styles.matchRow}>
                      <strong>Round:</strong>
                      <span>{linkedMatch.effective_round}</span>
                    </div>
                  ) : null}
                  {linkedMatch.effective_start_time ? (
                    <div className={styles.matchRow}>
                      <strong>Match Date/Time:</strong>
                      <span>
                        {formatToPacificTime(linkedMatch.effective_start_time)}
                      </span>
                    </div>
                  ) : null}
                  {linkedMatch.effective_status ? (
                    <div className={styles.matchRow}>
                      <strong>Match Status:</strong>
                      <span>{linkedMatch.effective_status}</span>
                    </div>
                  ) : null}
                  {linkedMatch.tc_match_id && (
                    <div className={styles.matchRow}>
                      <Link
                        to={`/matches/${linkedMatch.tc_match_id}`}
                        className={styles.matchLink}
                      >
                        View Match Details →
                      </Link>
                    </div>
                  )}
                </div>
              ) : null}
            </Panel>
          )}
        </div>

        {/* Right: publish state, renditions (info + preview per tab), thumbnails.
            Packaging state and the retry affordance live in the Pipeline panel
            below: showing them here too gave two headers that can disagree
            mid-refresh, and only the Pipeline panel knows which version. */}
        <div className={styles.rightColumn}>
          <Panel margin="none">
            <Panel.Header>
              <h2>Publishing</h2>
              {publishState && (
                <Badge variant={PUBLISH_STATE_VARIANT[publishState]}>
                  {PUBLISH_STATE_LABEL[publishState] || publishState}
                </Badge>
              )}
              {asset.archived_at && <Badge variant="neutral">Archived</Badge>}
              {supportsAmagiDelivery && (
                <Button
                  size="small"
                  variant="secondary"
                  className={styles.headerAction}
                  onClick={() => setShowAuditModal(true)}
                >
                  Audit Log
                </Button>
              )}
            </Panel.Header>
            <div className={styles.publishBand}>
              <div className={styles.publishActions}>
                {canPublish && (
                  <Button
                    size="small"
                    onClick={handlePublish}
                    disabled={isTogglingPublish}
                  >
                    {isTogglingPublish ? 'Publishing…' : 'Publish'}
                  </Button>
                )}
                {canUnpublish && (
                  <Button
                    size="small"
                    variant="secondary"
                    onClick={handleUnpublish}
                    disabled={isTogglingPublish}
                  >
                    {isTogglingPublish ? 'Unpublishing…' : 'Unpublish'}
                  </Button>
                )}
                {supportsAmagiDelivery && (
                  <Button
                    size="small"
                    variant="secondary"
                    onClick={handleResendToAmagi}
                    disabled={isResending}
                  >
                    {isResending ? 'Republishing to Amagi…' : 'Republish to Amagi'}
                  </Button>
                )}
              </div>
              {supportsAmagiDelivery && (
                <div className={styles.publishHint}>
                  Republishing re-announces the same media to Amagi as a new
                  attempt. The version tracks the media, so it only changes when
                  the video does.
                </div>
              )}
              {publishBlockedReason && (
                <div className={styles.publishHint}>{publishBlockedReason}</div>
              )}
              {visibilityBlockedReason && (
                <div className={styles.publishHint}>{visibilityBlockedReason}</div>
              )}
            </div>
          </Panel>

          <Panel margin="none">
            <Panel.Header>
              <h2>Renditions</h2>
            </Panel.Header>
            {totalTabs > 1 && (
              <div className={styles.tabs}>
                {showSourceTab && (
                  <button
                    type="button"
                    className={
                      isSourceActive
                        ? `${styles.tab} ${styles.tabActive}`
                        : styles.tab
                    }
                    onClick={() => {
                      setSelectedTabIdx(0)
                      setNativeFailed(false)
                    }}
                  >
                    Source
                  </button>
                )}
                {tabs.map((t, i) => (
                  <button
                    key={t.id}
                    type="button"
                    className={
                      i + sourceOffset === activeIdx
                        ? `${styles.tab} ${styles.tabActive}`
                        : styles.tab
                    }
                    onClick={() => {
                      setSelectedTabIdx(i + sourceOffset)
                      setNativeFailed(false)
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}

            {vodId && isSourceActive ? (
              <VodUploadPanel
                assetId={vodId}
                master={masterRendition}
                isPackaged={masterIsPackaged}
                expectedMasterVersion={masterVersion ?? 0}
                onUploadCompleted={() => mutate()}
              />
            ) : !activeTab ? (
              <div className={styles.emptyText}>No renditions.</div>
            ) : (
              <div className={styles.renditionRow}>
                <div className={styles.renditionKind}>
                  <strong>
                    {activeTab.rendition
                      ? activeTab.rendition.rendition_kind_display_name ||
                        activeTab.rendition.rendition_kind_code
                      : 'MP4 File'}
                  </strong>
                  {activeTab.rendition ? (
                    activeTab.rendition.is_playable ? (
                      <Badge variant="success">Playable</Badge>
                    ) : (
                      <Badge variant="neutral">Not playable</Badge>
                    )
                  ) : (
                    <Badge variant="neutral">Legacy field</Badge>
                  )}
                  {activeVersion !== null && (
                    <Badge variant={activeVersionLags ? 'warning' : 'neutral'}>
                      {activeVersionLags
                        ? `v${activeVersion} — master is v${masterVersion}`
                        : `v${activeVersion}`}
                    </Badge>
                  )}
                </div>

                {activeTab.rendition?.s3_bucket && (
                  <div className={styles.assetRow}>
                    <strong>Bucket:</strong>
                    <code className={styles.codeValue}>
                      {activeTab.rendition.s3_bucket}
                    </code>
                  </div>
                )}
                {activeTab.rendition?.s3_key && (
                  <div className={styles.assetRow}>
                    <strong>Key:</strong>
                    <code className={styles.codeValue}>
                      {activeTab.rendition.s3_key}
                    </code>
                  </div>
                )}
                {activeTab.rendition?.mime_type && (
                  <div className={styles.assetRow}>
                    <strong>MIME Type:</strong>
                    <span>{activeTab.rendition.mime_type}</span>
                  </div>
                )}
                {activeUrl && (
                  <div className={styles.assetRow}>
                    <strong>URL:</strong>
                    {isManifestKind(activeTab.label) ? (
                      <span className={styles.urlText}>{activeUrl}</span>
                    ) : (
                      <a
                        href={activeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.urlLink}
                      >
                        {activeUrl}
                      </a>
                    )}
                  </div>
                )}

                {activeTab.preview
                  ? renderPreview(activeTab.preview)
                  : activeNoPreviewReason && (
                      <div className={styles.previewFallback}>
                        <p>{activeNoPreviewReason}</p>
                      </div>
                    )}
              </div>
            )}
          </Panel>

          {/* Thumbnails — same slot editor the Highlight & Condensed page uses */}
          <Panel margin="none">
            <Panel.Header>
              <h2>Thumbnails</h2>
            </Panel.Header>
            <ImageManagement
              entityType="vod"
              entityId={asset.tc_vod_asset_id}
              defaultImageType="vod_card"
              onChange={() => mutate()}
            />
          </Panel>

          <SlugManager entityType="vod_asset" entityId={asset.tc_vod_asset_id} />
        </div>
      </div>

      {/* Full-width band: the collapsed ledger needs more width than a flex:1
          detail column gives it, and the MediaConvert id is the first column to
          elide. It also reads in the right order — metadata and preview answer
          "what is this", the pipeline answers "how did it get here". */}
      <div className={styles.pipelineBand}>
        <VodPipelinePanel
          pipeline={asset.pipeline}
          origin={asset.origin}
          onRetry={handleRetryPackaging}
          isRetrying={isRetryingPackaging}
        />
      </div>

      {/* Keyed by the asset: a VOD owns its publishing flow, so the audit
          trail is reachable even before a pub row exists. */}
      <PublishingAuditModal
        contentId={vodId!}
        source="vod"
        isOpen={showAuditModal}
        onClose={() => setShowAuditModal(false)}
      />
    </div>
  )
}

export default VodDetail
